import { LayoutGroup } from 'motion/react'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import type { CartItem } from 'stores/cartStore/CartItem'
import { getPricingItemsForCartItem } from '../../cart.utils'
import { useReachableHolidayByProductType } from '../../hooks/useReachableHolidayByProductType'
import { useGroupCheckoutPricing } from '../../hooks/useGroupCheckoutPricing'
import ProductSummaryRow from './ProductSummaryRow'
import * as S from './ProductSummarySegment.styles'

type ProductSummarySegmentProps = {
  items: CartItem[]
  onEdit: (item: CartItem) => void
  onQuantityChange: (item: CartItem, newQuantity: number) => void
  onRemove: (item: CartItem) => void
}

function ProductSummarySegment({
  items,
  onEdit,
  onQuantityChange,
  onRemove,
}: ProductSummarySegmentProps) {
  const groupPricing = useGroupCheckoutPricing()
  const isPricingLoading = groupPricing.isLoading
  const currency = groupPricing.data?.currency
  const pricingProducts = groupPricing.data?.products
  const experimentManager = useExperimentManager()
  // Read whenever the bag opens with items, since the treatment asks for its dates right then.
  const isCartHolidayCutoffsEnabled = experimentManager.isEnabled(
    'cart-holiday-cutoffs'
  )
  const reachableHolidayByProductType = useReachableHolidayByProductType({
    enabled: isCartHolidayCutoffsEnabled,
  })

  return (
    <S.Container
      data-testid="cart-product-summary"
      data-reachable-holiday-status={reachableHolidayByProductType.status}
    >
      <S.ItemList>
        <LayoutGroup>
          {items.map((item) => (
            <ProductSummaryRow
              key={item.id}
              item={item}
              currency={currency}
              matchedPricingProducts={
                pricingProducts &&
                getPricingItemsForCartItem(item, pricingProducts, items)
              }
              isPricingLoading={isPricingLoading}
              reachableHoliday={
                // Checkout's dates share the cache, so the control could read them too.
                isCartHolidayCutoffsEnabled
                  ? reachableHolidayByProductType.data?.[item.productType]
                  : undefined
              }
              onEdit={onEdit}
              onQuantityChange={onQuantityChange}
              onRemove={onRemove}
            />
          ))}
        </LayoutGroup>
      </S.ItemList>
    </S.Container>
  )
}

export default ProductSummarySegment
